import { tokenStorage } from "../../app/store/useTokenStorage";
import { BACKEND_URL } from "../constants/api.constants";
import { sanitizeVendorMessage } from "../utils/sanitizeVendorMessage";

const baseURL = BACKEND_URL;

type RefreshTokenResponse = {
  success: boolean;
  accessToken: string;
};

let refreshPromise: Promise<string> | null = null;

const refreshToken = async (): Promise<string> => {
  const response = await fetch(`${baseURL}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  };

  const data: RefreshTokenResponse = await response.json();
    
  if (data.success) {
    tokenStorage.setAccessToken(data.accessToken);
    return data.accessToken;
  };

  throw new Error('Token refresh failed');
};

const getRefreshedAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

export class FetchApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(sanitizeVendorMessage(message));
    this.name = 'FetchApiError';
    this.status = status;
    this.code = code;
  }
}

export async function fetchDataWithFetch<T>(
  url: string, 
  options: RequestInit & { data?: any; responseType?: 'json' | 'blob'; apiBaseUrl?: string } = {}
): Promise<T> {
  const { data, responseType = 'json', apiBaseUrl = BACKEND_URL, ...restOptions } = options;
  let accessToken = tokenStorage.getAccessToken();
      
  const config: RequestInit = {
    headers: {
      ...(data && !(data instanceof FormData) && { 'Content-Type': 'application/json' }),
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...options.headers,
    },
    credentials: 'include',
    ...restOptions,
  };

  if (data) {
    config.body = data instanceof FormData ? data : JSON.stringify(data);
  }

  let response = await fetch(`${apiBaseUrl}${url}`, config);

  if (response.status === 401) {
    try {
      accessToken = await getRefreshedAccessToken();
      
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${accessToken}`
      };
      
      response = await fetch(`${apiBaseUrl}${url}`, config);
    } catch (error) {
      tokenStorage.clearTokens();
      throw error;
    }
  };

  if (!response.ok) {
    if (responseType === 'blob') {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new FetchApiError(response.status, errorText || `HTTP error! status: ${response.status}`);
    }

    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
    const code = typeof errorData.code === 'string' ? errorData.code : undefined;
    throw new FetchApiError(response.status, message, code);
  };

  if (responseType === 'blob') {
    return response.blob() as Promise<T>;
  }
  
  return response.json() as Promise<T>;
}